<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onUnmounted, ref, watch, watchEffect } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { crop as cropPage } from '@/routes/tools/pdf';
import cropTool from '@/routes/tools/pdf/crop';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.crop.seo',
    fallbackTitleKey: 'tools.pdf.crop.title',
    fallbackTitleLabel: 'Crop PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: cropPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.crop.title',
                title: 'Crop PDF',
                href: cropPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const serverPageCount = ref<number | null>(null);
const pageCountLoading = ref(false);
const cropLeft = ref(0);
const cropRight = ref(0);
const cropTop = ref(0);
const cropBottom = ref(0);
const usePageRange = ref(false);
const ranges = ref('');
const status = ref<Status>('idle');
const statusMessage = ref('');
const previewPage = ref(1);

const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const overlayCanvas = ref<HTMLCanvasElement | null>(null);
const {
    isLoading: previewLoading,
    error: previewError,
    pageCount: pdfPageCount,
    pageWidthPt,
    pageHeightPt,
    previewZoomPct,
    loadFile,
    renderPage,
    unload,
} = usePdfPage();
const totalPreviewPages = computed(() =>
    Math.max(1, serverPageCount.value ?? pdfPageCount.value),
);

async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    serverPageCount.value = null;
    status.value = 'idle';

    if (!file.value) {
        unload();

        return;
    }

    pageCountLoading.value = true;
    const fd = new FormData();
    fd.append('file', file.value);

    try {
        const r = await axios.post(cropTool.pageCount.url(), fd);
        serverPageCount.value = r.data.page_count as number;
    } catch {
        serverPageCount.value = null;
    } finally {
        pageCountLoading.value = false;
    }

    await loadFile(file.value);
    await nextTick();

    if (!pdfCanvas.value) {
        return;
    }

    previewPage.value = 1;
    await renderPreviewPage(1);
}

function removeFile(): void {
    file.value = null;
    serverPageCount.value = null;
    status.value = 'idle';
    previewPage.value = 1;
    unload();
}

async function renderPreviewPage(page: number): Promise<void> {
    if (!pdfCanvas.value) {
        return;
    }

    const clamped = Math.min(totalPreviewPages.value, Math.max(1, page));
    previewPage.value = clamped;
    await renderPage(pdfCanvas.value, clamped, 440);
    await nextTick();
    drawCropOverlay();
}

watch(previewZoomPct, () => {
    if (!file.value || !pdfCanvas.value) {
        return;
    }

    void renderPreviewPage(previewPage.value);
});

function drawCropOverlay(): void {
    if (!pdfCanvas.value || !overlayCanvas.value || !file.value) {
        return;
    }

    const pw = pdfCanvas.value.width;
    const ph = pdfCanvas.value.height;

    if (pw === 0 || ph === 0) {
        return;
    }

    overlayCanvas.value.width = pw;
    overlayCanvas.value.height = ph;

    const ctx = overlayCanvas.value.getContext('2d')!;
    ctx.clearRect(0, 0, pw, ph);

    const mmW = pageWidthPt.value * 0.352778;
    const mmH = pageHeightPt.value * 0.352778;

    if (mmW === 0 || mmH === 0) {
        return;
    }

    const pxPerMmX = pw / mmW;
    const pxPerMmY = ph / mmH;

    const lPx = cropLeft.value * pxPerMmX;
    const rPx = cropRight.value * pxPerMmX;
    const tPx = cropTop.value * pxPerMmY;
    const bPx = cropBottom.value * pxPerMmY;

    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';

    if (tPx > 0) {
        ctx.fillRect(0, 0, pw, tPx);
    }

    if (bPx > 0) {
        ctx.fillRect(0, ph - bPx, pw, bPx);
    }

    if (lPx > 0) {
        ctx.fillRect(0, tPx, lPx, ph - tPx - bPx);
    }

    if (rPx > 0) {
        ctx.fillRect(pw - rPx, tPx, rPx, ph - tPx - bPx);
    }

    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2;
    ctx.strokeRect(lPx, tPx, pw - lPx - rPx, ph - tPx - bPx);
}

watchEffect(() => {
    void cropLeft.value;
    void cropRight.value;
    void cropTop.value;
    void cropBottom.value;
    void previewPage.value;
    void pageWidthPt.value;
    void pageHeightPt.value;
    void pdfCanvas.value;
    void overlayCanvas.value;
    drawCropOverlay();
});

onUnmounted(unload);

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());
            const msgs = Object.values(parsed.errors ?? {}).flat();

            return (
                (msgs[0] as string) ||
                t('tools.pdf.ui.crop_failed', 'Crop failed.')
            );
        } catch {
            return t('tools.pdf.ui.crop_failed', 'Crop failed.');
        }
    }

    return t('tools.pdf.ui.crop_failed', 'Crop failed.');
}

async function crop(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.cropping', 'Cropping PDF...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('crop_left', String(cropLeft.value));
    formData.append('crop_right', String(cropRight.value));
    formData.append('crop_top', String(cropTop.value));
    formData.append('crop_bottom', String(cropBottom.value));

    if (usePageRange.value && ranges.value.trim()) {
        formData.append('pages', ranges.value);
    }

    try {
        const response = await axios.post(cropTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'cropped.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.crop_success',
            'PDF cropped successfully!',
        );
        file.value = null;
        serverPageCount.value = null;
        unload();
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-crop" />

    <ToolLayout
        :title="t('tools.pdf.crop.title', 'Crop PDF')"
        :subtitle="t('tools.pdf.crop.description', '')"
        :show-how-to="false"
    >
        <div class="grid gap-4 lg:grid-cols-[2fr_3fr]">
            <!-- ── Left: preview ── -->
            <div class="lg:sticky lg:top-4 lg:self-start">
                <PdfLivePreview
                    v-model:zoom="previewZoomPct"
                    :has-file="!!file"
                    :loading="previewLoading || pageCountLoading"
                    :error="previewError"
                    :current-page="previewPage"
                    :total-pages="totalPreviewPages"
                    :rendering-message="
                        t('tools.pdf.ui.rendering', 'Rendering…')
                    "
                    @change-page="renderPreviewPage"
                >
                    <canvas
                        ref="pdfCanvas"
                        class="relative z-0 block max-h-none w-full bg-white dark:bg-gray-950"
                    />
                    <canvas
                        ref="overlayCanvas"
                        class="absolute inset-0 z-10 h-full w-full"
                        style="pointer-events: none"
                    />
                    <template #footer>
                        <p
                            v-if="file && !previewLoading"
                            class="mt-2 text-xs text-gray-400 dark:text-gray-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.crop_footer_hint',
                                    'Dark areas show the cropped regions. The blue border marks the kept area.',
                                )
                            }}
                        </p>
                    </template>
                </PdfLivePreview>
                <div class="mt-4">
                    <ToolHowToCard />
                </div>
            </div>

            <!-- ── Right: controls ── -->
            <div class="space-y-4">
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf',
                                'Click or drag a PDF here',
                            )
                        "
                        :sublabel="
                            lineWithMax(
                                t('tools.pdf.ui.one_pdf_file', 'One PDF file'),
                            )
                        "
                        @update:model-value="onFileSelected"
                    />
                    <div v-if="file" class="mt-3">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                </div>

                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <p
                        class="mb-4 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{
                            t(
                                'tools.pdf.ui.crop_margins_title',
                                'Crop margins (mm)',
                            )
                        }}
                    </p>

                    <div class="flex flex-col items-center gap-2">
                        <div class="w-32">
                            <label
                                class="mb-1 block text-center text-xs text-gray-500"
                                >{{ t('tools.pdf.ui.crop_top', 'Top') }}</label
                            >
                            <input
                                v-model.number="cropTop"
                                type="number"
                                min="0"
                                placeholder="0"
                                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-center text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            />
                        </div>

                        <div class="flex w-full items-center gap-2">
                            <div class="flex-1">
                                <label
                                    class="mb-1 block text-center text-xs text-gray-500"
                                    >{{
                                        t('tools.pdf.ui.crop_left', 'Left')
                                    }}</label
                                >
                                <input
                                    v-model.number="cropLeft"
                                    type="number"
                                    min="0"
                                    placeholder="0"
                                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-center text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                                />
                            </div>
                            <div
                                class="flex h-12 w-16 flex-shrink-0 items-center justify-center rounded border-2 border-dashed border-gray-300 text-xs text-gray-400 dark:border-gray-600"
                            >
                                {{ t('tools.pdf.ui.crop_page_label', 'Page') }}
                            </div>
                            <div class="flex-1">
                                <label
                                    class="mb-1 block text-center text-xs text-gray-500"
                                    >{{
                                        t('tools.pdf.ui.crop_right', 'Right')
                                    }}</label
                                >
                                <input
                                    v-model.number="cropRight"
                                    type="number"
                                    min="0"
                                    placeholder="0"
                                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-center text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                                />
                            </div>
                        </div>

                        <div class="w-32">
                            <label
                                class="mb-1 block text-center text-xs text-gray-500"
                                >{{
                                    t('tools.pdf.ui.crop_bottom', 'Bottom')
                                }}</label
                            >
                            <input
                                v-model.number="cropBottom"
                                type="number"
                                min="0"
                                placeholder="0"
                                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-center text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            />
                        </div>
                    </div>

                    <div class="mt-5 border-t border-border pt-4">
                        <label
                            class="flex cursor-pointer items-start gap-3.5 select-none"
                        >
                            <Checkbox
                                v-model="usePageRange"
                                class="mt-0.5 shrink-0"
                            />
                            <span
                                class="text-sm leading-snug text-gray-700 dark:text-gray-300"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.crop_apply_pages_only',
                                        'Apply to specific pages only',
                                    )
                                }}
                            </span>
                        </label>

                        <div v-if="usePageRange" class="mt-3">
                            <input
                                v-model="ranges"
                                type="text"
                                :placeholder="
                                    t(
                                        'tools.pdf.ui.crop_pages_placeholder',
                                        'e.g. 1-3, 5, 7-9',
                                    )
                                "
                                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                            />
                            <p
                                class="mt-1 text-xs text-gray-400 dark:text-gray-500"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.crop_pages_hint',
                                        'Other pages are kept unchanged. Use commas and hyphens:',
                                    )
                                }}
                                <code>1-3, 5</code>
                            </p>
                        </div>
                    </div>
                </div>

                <Button
                    class="w-full bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="!file || status === 'loading'"
                    @click="crop"
                >
                    {{ t('tools.pdf.ui.crop_pdf_button', 'Crop PDF') }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
